import { useState, useCallback, useMemo } from 'react'
import { ChevronLeft, ChevronRight, Loader2, Copy, Check } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@quebon/ui/components/dialog'
import { quizScriptApi } from '@/api/quizScript'
import type { QuizScript, QuizSolveRequest, SolveResult } from '@quebon/shared-learning'
import {
  QuizRenderer,
  DifficultyBadge,
} from '@quebon/shared-learning'
import type {
  QuizSolveResult,
  MultiChoiceQuizData,
  QuizData,
} from '@quebon/shared-learning'

export type NavPolicy = 'free_move' | 'lock_next'

export interface QuizDebugContext {
  lessonId?: number
  lessonTitle?: string
  moduleName?: string
  moduleId?: number
  courseName?: string
  courseId?: number
  curriculumName?: string
  curriculumId?: number
  contentId?: number
  qid?: string
}

interface TotalQuizSessionProps {
  quizList: QuizScript[]
  navPolicy?: NavPolicy
  title?: string
  onClose?: () => void
  isLoading?: boolean
  debugContext?: QuizDebugContext
}

type QuizStatus = 'unsolved' | 'current' | 'correct' | 'incorrect'

export function TotalQuizSession({
  quizList,
  navPolicy = 'free_move',
  title,
  onClose,
  isLoading = false,
  debugContext,
}: TotalQuizSessionProps) {
  const { t } = useTranslation('learner')
  const [currentIndex, setCurrentIndex] = useState(0)
  const [resultsByQuizId, setResultsByQuizId] = useState<Map<string, QuizSolveResult>>(new Map())
  const [submittedAnswersByQuizId, setSubmittedAnswersByQuizId] = useState<Map<string, string[]>>(new Map())
  const [isSolving, setIsSolving] = useState(false)
  const [showErrorReportDialog, setShowErrorReportDialog] = useState(false)

  const currentQuiz = quizList[currentIndex]
  const currentQuizId = currentQuiz?.quizId ?? ''
  const currentResult = resultsByQuizId.get(String(currentQuizId))
  const currentSubmittedAnswers = submittedAnswersByQuizId.get(String(currentQuizId))

  const getQuizStatus = useCallback((index: number): QuizStatus => {
    if (index === currentIndex) return 'current'

    const quiz = quizList[index]
    const quizId = String(quiz?.quizId ?? '')
    const result = resultsByQuizId.get(quizId)

    if (!result) return 'unsolved'
    return result.correct ? 'correct' : 'incorrect'
  }, [currentIndex, quizList, resultsByQuizId])

  const getLegacyQuizData = useCallback((quizScript: QuizScript): QuizData | null => {
    if (!quizScript) return null

    const rawData = quizScript as unknown as Record<string, unknown>
    const qd = (rawData.quizData as QuizData) || quizScript

    return {
      ...qd,
      type: quizScript.type || qd.type,
      title: quizScript.title || qd.title,
      problem: quizScript.problem || qd.problem,
      vars: quizScript.vars || qd.vars,
      image: quizScript.image || qd.image,
      imageMobile: quizScript.imageMobile || qd.imageMobile,
    } as QuizData
  }, [])

  const handleSolve = useCallback(
    async (
      _quizId: string | number,
      answers: string[],
      quizVars: Record<string, unknown>,
      elapsedTime: number
    ) => {
      if (!currentQuiz || isSolving) return

      const quizId = String(currentQuiz.quizId)

      setSubmittedAnswersByQuizId(prev => new Map(prev).set(quizId, answers))
      setIsSolving(true)

      const legacyQuiz = getLegacyQuizData(currentQuiz)
      if (!legacyQuiz) {
        setIsSolving(false)
        return
      }

      try {
        let transformedAnswers: (string | number | boolean)[] = []

        switch (legacyQuiz.type) {
          case 'fill_blank':
            transformedAnswers = answers
            break

          case 'multi_choice': {
            const quiz = legacyQuiz as MultiChoiceQuizData
            transformedAnswers = answers
              .map((key) => {
                const index = quiz.choices.findIndex((choice) => choice.key === key)
                return index >= 0 ? index + 1 : 0
              })
              .filter((idx) => idx > 0)
            break
          }

          case 'select_text': {
            transformedAnswers = answers.map((a) => {
              const num = parseInt(a, 10)
              return isNaN(num) ? 0 : num
            })
            break
          }

          case 'boolean': {
            transformedAnswers = answers.map((a) => a === 'true')
            break
          }

          case 'drag_text':
            transformedAnswers = answers
            break

          default:
            transformedAnswers = []
        }

        const request: QuizSolveRequest = {
          answers: transformedAnswers,
          vars: quizVars,
          elapsed: elapsedTime,
        }

        const apiResult: SolveResult = await quizScriptApi.solve(quizId, request)

        const rawApiResult = apiResult as unknown as Record<string, unknown>
        const apiElapsed = (rawApiResult.elapsed as number) ?? elapsedTime

        const result: QuizSolveResult = {
          correct: apiResult.correct,
          corrects: apiResult.corrects.map((c) => {
            if (typeof c === 'boolean') return c
            if (c === 'true' || c === true) return true
            if (c === 'false' || c === false) return false
            return Boolean(c)
          }),
          correctAnswers: (apiResult.correctAnswers ?? []).map((a) => {
            if (typeof a === 'string') return a
            if (typeof a === 'number') return String(a)
            if (typeof a === 'boolean') return String(a)
            return String(a)
          }),
          elapsedTime: apiElapsed,
          description: apiResult.description
            ? {
              text: apiResult.description.text ?? undefined,
              image: (() => {
                const desc = apiResult.description as Record<string, unknown>
                const img = desc.image
                if (typeof img === 'string') return { signedUrl: img }
                if (img && typeof img === 'object') return img as { signedUrl: string }
                return undefined
              })(),
              imageMobile: (() => {
                const desc = apiResult.description as Record<string, unknown>
                const img = desc.imageMobile
                if (typeof img === 'string') return { signedUrl: img }
                if (img && typeof img === 'object') return img as { signedUrl: string }
                return undefined
              })(),
              desmos: apiResult.description.desmos ?? undefined,
            }
            : undefined,
        }

        setResultsByQuizId(prev => new Map(prev).set(quizId, result))
      } catch (err) {
        console.error('Failed to solve quiz:', err)

        const result: QuizSolveResult = {
          correct: false,
          corrects: [],
          correctAnswers: [],
          elapsedTime,
        }
        setResultsByQuizId(prev => new Map(prev).set(quizId, result))
      } finally {
        setIsSolving(false)
      }
    },
    [currentQuiz, isSolving, getLegacyQuizData]
  )

  const handlePrevious = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1)
    }
  }, [currentIndex])

  const handleNext = useCallback(() => {
    if (currentIndex < quizList.length - 1) {
      setCurrentIndex(currentIndex + 1)
    }
  }, [currentIndex, quizList.length])

  const canGoNext = useMemo(() => {
    if (currentIndex >= quizList.length - 1) return false
    if (navPolicy === 'free_move') return true
    return resultsByQuizId.has(String(currentQuizId))
  }, [currentIndex, quizList.length, navPolicy, resultsByQuizId, currentQuizId])

  const handleNavClick = useCallback((index: number) => {
    if (index === currentIndex) return
    setCurrentIndex(index)
  }, [currentIndex])

  const legacyQuiz = currentQuiz ? getLegacyQuizData(currentQuiz) : null

  // 디버그 정보 생성 (퀴즈 정보 + lesson 정보)
  const getErrorReportText = useCallback(() => {
    const quizInfo: Record<string, unknown> = {}

    // 현재 퀴즈 정보
    if (currentQuiz) {
      quizInfo.quizId = String(currentQuiz.quizId)
      quizInfo.questionNumber = `${currentIndex + 1} / ${quizList.length}`
      quizInfo.quizType = currentQuiz.type
      if (currentQuiz.title) quizInfo.quizTitle = currentQuiz.title
    }

    // 레슨/컨텍스트 정보
    if (debugContext?.qid) quizInfo.qid = debugContext.qid
    if (debugContext?.lessonId) quizInfo.lessonId = debugContext.lessonId
    if (debugContext?.lessonTitle) quizInfo.lessonTitle = debugContext.lessonTitle
    if (debugContext?.moduleId) quizInfo.moduleId = debugContext.moduleId
    if (debugContext?.moduleName) quizInfo.moduleName = debugContext.moduleName
    if (debugContext?.courseId) quizInfo.courseId = debugContext.courseId
    if (debugContext?.courseName) quizInfo.courseName = debugContext.courseName
    if (debugContext?.curriculumId) quizInfo.curriculumId = debugContext.curriculumId
    if (debugContext?.curriculumName) quizInfo.curriculumName = debugContext.curriculumName
    if (debugContext?.contentId) quizInfo.contentId = debugContext.contentId
    quizInfo.copiedAt = new Date().toISOString()

    const lines = [`=== ${t('learning.totalQuiz.errorReportTitle')} ===`]
    if (currentQuiz) {
      lines.push(`quizId: ${String(currentQuiz.quizId)}`)
      lines.push(`${t('learning.totalQuiz.questionNumber')}: ${currentIndex + 1} / ${quizList.length}`)
      lines.push(`${t('learning.totalQuiz.typeLabel')}: ${currentQuiz.type}`)
      if (currentQuiz.title) lines.push(`${t('learning.totalQuiz.title')}: ${currentQuiz.title}`)
      lines.push('')
    }
    if (debugContext?.qid) lines.push(`qid: ${debugContext.qid}`)
    if (debugContext?.lessonId) lines.push(`lessonId: ${debugContext.lessonId}`)
    if (debugContext?.lessonTitle) lines.push(`lessonTitle: ${debugContext.lessonTitle}`)
    if (debugContext?.moduleName) lines.push(`moduleName: ${debugContext.moduleName}`)
    if (debugContext?.moduleId) lines.push(`moduleId: ${debugContext.moduleId}`)
    if (debugContext?.courseName) lines.push(`courseName: ${debugContext.courseName}`)
    if (debugContext?.courseId) lines.push(`courseId: ${debugContext.courseId}`)
    if (debugContext?.curriculumName) lines.push(`curriculumName: ${debugContext.curriculumName}`)
    if (debugContext?.curriculumId) lines.push(`curriculumId: ${debugContext.curriculumId}`)
    if (debugContext?.contentId) lines.push(`contentId: ${debugContext.contentId}`)

    lines.push('')
    lines.push('=== JSON (상세) ===')
    lines.push(JSON.stringify(quizInfo, null, 2))

    return lines.join('\n')
  }, [debugContext, currentQuiz, currentIndex, quizList.length])

  const handleCopyAndCloseDialog = useCallback(async () => {
    const textToCopy = getErrorReportText()
    if (!textToCopy) return

    try {
      await navigator.clipboard.writeText(textToCopy)
      setShowErrorReportDialog(false)
    } catch (err) {
      console.error('Failed to copy debug info:', err)
    }
  }, [getErrorReportText])

  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center py-20 h-full">
        <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
        <p className="mt-4 text-gray-600">{t('learning.totalQuiz.loadingQuiz')}</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      {title && (
        <div className="flex justify-between items-start px-6 py-4 bg-white border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            <p className="mt-1 text-sm text-gray-500">
              {quizList.length > 0 ? `${currentIndex + 1} / ${quizList.length} ${t('learning.totalQuiz.items', { count: quizList.length })}` : t('learning.totalQuiz.noQuiz')}
            </p>
          </div>
          <button
            onClick={() => setShowErrorReportDialog(true)}
            className="flex gap-1.5 items-center px-3 py-1.5 text-xs font-medium rounded-lg transition-all bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800"
            title={t('learning.totalQuiz.debugInfoTitle')}
          >
            <Copy className="w-3.5 h-3.5" />
            {t('learning.totalQuiz.errorReport')}
          </button>
        </div>
      )}

      {quizList.length === 0 ? (
        <div className="flex flex-col flex-1 justify-center items-center py-20">
          <p className="text-gray-500">{t('learning.totalQuiz.noQuizzesInLesson')}</p>
        </div>
      ) : (
        <>
          <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
            <div className="flex overflow-x-auto gap-2 p-2 custom-scrollbar">
              {quizList.map((quiz, index) => {
                const status = getQuizStatus(index)
                const quizId = String(quiz.quizId ?? index)
                const dl = quiz.difficultyLevel

                return (
                  <div key={quizId} className="flex flex-col flex-shrink-0 gap-0.5 items-center">
                    <button
                      onClick={() => handleNavClick(index)}
                      className={cn(
                        'w-10 h-10 rounded-lg font-medium text-sm transition-all',
                        status === 'unsolved' && 'border-2 border-gray-300 bg-white text-gray-600 hover:border-gray-400',
                        status === 'current' && 'ring-2 ring-indigo-500 ring-offset-2 bg-indigo-600 text-white',
                        status === 'correct' && 'bg-emerald-500 text-white',
                        status === 'incorrect' && 'bg-rose-500 text-white'
                      )}
                    >
                      {index + 1}
                    </button>
                    {dl != null && (
                      <span className={cn(
                        'text-[9px] font-bold leading-none',
                        dl === 1 && 'text-emerald-600',
                        dl === 2 && 'text-sky-600',
                        dl === 3 && 'text-amber-600',
                        dl === 4 && 'text-orange-600',
                        dl === 5 && 'text-rose-600',
                        ![1,2,3,4,5].includes(dl) && 'text-gray-500',
                      )}>
                        Lv.{dl}
                      </span>
                    )}
                  </div>
                )
              })}
            </div>
          </div>

          <div className="overflow-y-auto flex-1 p-6">
        {legacyQuiz && (
          <div className="mx-auto max-w-3xl">
            <QuizRenderer
              key={`${currentQuizId}-${currentResult ? 'solved' : 'unsolved'}`}
              quizType={legacyQuiz.type}
              quizId={currentQuizId}
              quiz={legacyQuiz}
              onSolve={handleSolve}
              solveResult={currentResult}
              showReallyTrue={false}
              useKeypad={false}
              initialAnswers={currentSubmittedAnswers}
              difficultyLevel={currentQuiz?.difficultyLevel}
            />

            {isSolving && (
              <div className="flex gap-2 justify-center items-center mt-4 text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">{t('learning.totalQuiz.grading')}</span>
              </div>
            )}
          </div>
        )}
          </div>

          <div className="px-6 py-4 bg-white border-t border-gray-200">
            <div className="flex justify-between items-center mx-auto max-w-3xl">
              <button
                onClick={handlePrevious}
                disabled={currentIndex === 0}
                className={cn(
                  'flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors',
                  currentIndex === 0
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-700 hover:bg-gray-100'
                )}
              >
                <ChevronLeft className="w-5 h-5" />
                {t('learning.totalQuiz.previous')}
              </button>

              <span className="text-sm text-gray-500">
                {currentIndex + 1} / {quizList.length}
              </span>

              <button
                onClick={handleNext}
                disabled={!canGoNext}
                className={cn(
                  'flex gap-2 items-center px-4 py-2 font-medium rounded-lg transition-colors',
                  !canGoNext
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-indigo-600 hover:bg-indigo-50'
                )}
              >
                {t('learning.totalQuiz.next')}
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </>
      )}

      <Dialog open={showErrorReportDialog} onOpenChange={setShowErrorReportDialog}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>{t('learning.totalQuiz.errorReport')}</DialogTitle>
          </DialogHeader>
          <pre className="p-4 font-mono text-xs text-gray-800 whitespace-pre-wrap break-words bg-gray-50 rounded-lg border border-gray-200">
            {getErrorReportText()}
          </pre>
          <DialogFooter>
            <button
              onClick={handleCopyAndCloseDialog}
              className="flex gap-2 items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg transition-colors hover:bg-indigo-700"
            >
              <Copy className="w-4 h-4" />
              {t('learning.totalQuiz.copyContent')}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
